import BaseModel from "@/api/models/BaseModel";
import { env } from "@/composables/config";
import { useSessionMessageStore } from "./stores/sessionMessage";
import { sleep } from "./composables/sleep";
import { translate } from "./composables/translate";
import urlHandler from "./composables/urlHandler";

/**
 * SecurityManager
 * Adds additional security measures
 */
export default class SecurityManager extends BaseModel {

  constructor() {
    super();

    this.max_security     = env('APP_MAX_SECURITY');
    this.redirect_to      = env('APP_REDIRECT_TO_SECURITY');
    this.redirect_message = env('APP_REDIRECT_SECURITY_MESSAGE');

    this.disableDevTools = this.disableDevTools.bind(this);
    this.preventContextMenu = this.preventContextMenu.bind(this);
  }

  /**
   * Checks if max_security is true
   * @returns {Boolean}
   */
  mustRaiseSecurity() {
    return !!this.max_security;
  }

  /**
   * Verifica se o dispositivo atual é mobile/tablet ou tela pequena
   * Evita falsos positivos com teclados virtuais e zooms de smartphones
   * @returns {Boolean}
   */
  isMobileOrSmallScreen() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isMobileRegex = /android|avantgo|blackberry|iemobile|ipad|iphone|ipod|j2me|opera mini|opera mobi|palm|phone|pocket|psp|symbian|smartphone|tablet|up\.browser|up\.link|vodafone|wap|windows phone|xda|xiino/i;
    
    return isMobileRegex.test(userAgent) || window.innerWidth < 1024;
  }

  /**
   * Sets some security measures to application
   */
  raiseMaxSecurity() {
    if (!this.mustRaiseSecurity()) return;

    this.disableConsoleLogs();

    if (this.isMobileOrSmallScreen()) {
      return;
    }

    this.detectDevTools();

    document.addEventListener("keydown", this.disableDevTools);
    document.addEventListener("contextmenu", this.preventContextMenu);
  }

  /**
   * Disables console.logs
   */
  disableConsoleLogs() {
    const emptyLog = () => {};
    console.log = emptyLog;
    console.warn = emptyLog;
    console.error = emptyLog;
    console.info = emptyLog;
  }

  /**
   * Prevents right click
   */
  preventContextMenu(event) {
    event.preventDefault();
  }

  /**
   * Disables keyboard shortcuts for Windows, Linux and macOS
   */
  disableDevTools(event) {
    const key = event.key.toUpperCase();
    const isModifier = event.ctrlKey || event.metaKey; // metaKey pega o Command (⌘) no Mac/Safari
    const isShift = event.shiftKey;

    if (
      key === "F12" ||
      (isModifier && isShift && (key === "I" || key === "J" || key === "C")) ||
      (isModifier && key === "U") ||
      (isModifier && key === "S")
    ) {
      event.preventDefault();
      this.triggerRedirect();
    }
  }

  /**
   * Detects if DevTools are open, redirecting the page if true
   */
  async detectDevTools() {
    const threshold = 160;
    await sleep(500);

    this.detectPreOpenedDevTools();

    setInterval(() => {
      if (this.isMobileOrSmallScreen()) return;

      this.handleDevToolsDimensions(threshold);
    }, 1000);
  }

  /**
   * Detecta se o DevTools já estava aberto usando a brecha do método toString/getter
   */
  detectPreOpenedDevTools() {
    const devtoolsTest = /./;
    devtoolsTest.toString = () => {
      this.triggerRedirect();
      return '';
    };
    
    // Dispara a avaliação em background
    console.log(devtoolsTest); 
  }

  /**
   * Redirects to url based on window dimension changes
   * @param {integer} threshold 
   */
  handleDevToolsDimensions(threshold) {
    const widthThreshold = window.outerWidth - window.innerWidth > threshold;
    const heightThreshold = window.outerHeight - window.innerHeight > threshold;

    if (widthThreshold || heightThreshold) {
      this.triggerRedirect();
    }
  }

  /**
   * Centralizes the redirection logic
   */
  triggerRedirect() {
    const sessionMessageStore = useSessionMessageStore();
    sessionMessageStore.errorMessage(translate(this.redirect_message, 'ucfirst'));
    urlHandler.redirectAndScroll(this.redirect_to);
  }

  /**
   * Details in how to obfuscate code
   */
  offuscateCode() {
    // Execute at the terminal: javascript-obfuscator ./dist --output ./dist-obfuscated
  }
}